import * as Sentry from '@sentry/svelte';
import { BrowserTracing } from '@sentry/tracing';
import { ExtraErrorData } from '@sentry/integrations';

import { PUBLIC_APP_ENV, PUBLIC_SENTRY_DSN } from '$env/static/public';
import { Replay } from '@sentry/svelte';
import { dev, version } from '$app/environment';
import { page } from '$app/stores';

const errorEntities = [
	'qualtrics',
	'usercentrics',
	'google-analytics',
	'google-tag-manager',
	'doubleclick.net'
];

Sentry.init({
	debug: false,
	dsn: PUBLIC_SENTRY_DSN,
	environment: PUBLIC_APP_ENV,
	integrations: [new BrowserTracing(), new ExtraErrorData(), new Replay()],
	tracesSampleRate: 0.1,
	replaysSessionSampleRate: dev ? 1 : 0.01,
	replaysOnErrorSampleRate: dev ? 1 : 0.1,
	normalizeDepth: 0,
	release: version,
	beforeSend: (event: Sentry.Event) => {
		// On Cloudflare Pages/Workers, "allowUrls" and "denyUrls" are not working due to the adapter, so we have to do it manually.
		const breadcrumbs = event.breadcrumbs || [];

		console.log('sentry event.exception', event.exception);

		if (checkBreadcrumbsForErrors(breadcrumbs)) {
			console.log(
				'Event dropped: error originating from known error entity detected in breadcrumbs.'
			);
			return null;
		} else if (
			event.exception?.values?.[0]?.stacktrace?.frames?.[0]?.filename?.includes('usercentrics') ||
			event.exception?.values?.[0]?.value?.includes('usercentrics.eu')
		) {
			console.log('Event dropped: usercentrics.');
			return null;
		} else if (
			event.exception?.values?.[0]?.stacktrace?.frames?.[0]?.filename?.includes('tagmanager')
		) {
			console.log('Event dropped: tagmanager.');
			return null;
		} else if (
			event.exception?.values?.[0]?.stacktrace?.frames?.[0]?.filename?.includes('doubleclick.net')
		) {
			console.log('Event dropped: doubleclick.net.');
			return null;
		} else if (
			event.exception?.values?.[0]?.stacktrace?.frames?.[0]?.filename?.includes(
				'google-analytics.com'
			)
		) {
			console.log('Event dropped: google-analytics.com.');
			return null;
		} else if (
			event.exception?.values?.[0]?.stacktrace?.frames?.[0]?.filename?.includes('qualtrics')
		) {
			console.log('Event dropped: qualtrics.');
			return null;
		} else if (event.exception?.values?.[0]?.value?.includes('lpTag.newPage is not a function')) {
			console.log('Event dropped: common lpTag.newPage error.');
			return null;
		} else if (event.exception?.values?.[0]?.value?.includes('lpTag')) {
			console.log('Event dropped: common lpTag variable cannot be found error.');
			return null;
		}

		return event;
	},
	allowUrls: [/schalke04\.de/i],
	denyUrls: [
		// Qualtrics,
		/qualtrics\.com/i,
		/siteintercept.qualtrics.com/i,
		// Usercentrics
		/usercentrics\.eu/i,
		/api.usercentrics.eu/i,
		// Google Tag Manager et al
		/\/(gtm|ga|analytics)\.js/i,
		/googletagmanager\.com/i,
		// Chrome extensions
		/extensions\//i,
		/^chrome:\/\//i,
		/^chrome-extension:\/\//i
	]
});
Sentry.setTag('client', true);

page.subscribe((page) => {
	Sentry.setUser({
		id: page?.data?.sid
	});
});

const checkBreadcrumbsForErrors = (breadcrumbs: Sentry.Breadcrumb[]) => {
	const foundEntity = breadcrumbs.find((breadcrumb: Sentry.Breadcrumb) => {
		if (breadcrumb.data?.url) {
			return errorEntities.find((entity) => {
				return breadcrumb.data?.url.includes(entity);
			});
		}
	});

	const foundError = breadcrumbs.filter((breadcrumb: Sentry.Breadcrumb) => {
		return breadcrumb.level === 'error';
	});

	return foundEntity && foundError.length > 0 ? foundEntity : false;
};

const makeCapture = (level: 'error' | 'debug' | 'info' | 'warning') => {
	return (message: string, data?: Record<string, unknown>) => {
		Sentry.captureMessage(message, {
			level,
			extra: data
		});
	};
};

export const ClientLogger = {
	instance: Sentry,
	error: makeCapture('error'),
	debug: makeCapture('debug'),
	info: makeCapture('info'),
	warning: makeCapture('warning')
};
